import { useEffect, useState } from "react";
import { ChevronRight, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Clubhouse_LeagueType,
  Clubhouse_LeagueScoring,
  Clubhouse_LeagueV2Status,
} from "../../enums";
import { DeleteDraftButton } from "../common/DeleteDraftButton";
import { leagueTypeLabel, formatSeasonWindow } from "../helpers";
import {
  progressPercent,
  formatStatusLine,
  type DashboardLeague,
  type DashboardView,
} from "./dashboardViewModel";

const FORMAT_TAG_LABEL: Record<Clubhouse_LeagueScoring, string> = {
  [Clubhouse_LeagueScoring.STROKE]: "Stroke Play",
  [Clubhouse_LeagueScoring.MATCH]: "Match Play",
  [Clubhouse_LeagueScoring.POINTS]: "Points",
};

const TILE_BY_FORMAT: Record<
  "STROKE" | "MATCH" | "POINTS" | "SCRAMBLE",
  { letters: string; bg: string; text: string }
> = {
  STROKE: { letters: "SP", bg: "bg-good-soft", text: "text-good" },
  MATCH: { letters: "MP", bg: "bg-sky-soft", text: "text-sky" },
  POINTS: { letters: "PTS", bg: "bg-warn-soft", text: "text-warn" },
  SCRAMBLE: { letters: "SCR", bg: "bg-violet-soft", text: "text-violet" },
};

function tileFor(
  type: Clubhouse_LeagueType | undefined,
  scoringFormat: Clubhouse_LeagueScoring | undefined,
) {
  if (type === Clubhouse_LeagueType.SCRAMBLE) return TILE_BY_FORMAT.SCRAMBLE;
  if (scoringFormat === Clubhouse_LeagueScoring.MATCH)
    return TILE_BY_FORMAT.MATCH;
  if (scoringFormat === Clubhouse_LeagueScoring.POINTS)
    return TILE_BY_FORMAT.POINTS;
  return TILE_BY_FORMAT.STROKE;
}

const DOT_CLASS: Record<"gold" | "green" | "gray", string> = {
  gold: "bg-secondary",
  green: "bg-good",
  gray: "bg-text-faint",
};

interface LeagueRowProps {
  league: DashboardLeague;
  view: DashboardView;
  onOpen: (leagueId: string) => void;
  /** Without it the row never offers delete, whatever its status. */
  onDelete?: (league: DashboardLeague) => void;
}

// Only an Upcoming row can be a draft, so only that view has the action cell.
// Finalized rows there keep the cell empty so the arrows stay in one column.
const GRID_COLS: Record<"withAction" | "withoutAction", string> = {
  withAction:
    "md:grid-cols-[2.75rem_1fr_minmax(0,14.5rem)_minmax(0,15.5rem)_2rem_1rem]",
  withoutAction:
    "md:grid-cols-[2.75rem_1fr_minmax(0,14.5rem)_minmax(0,15.5rem)_1rem]",
};

export function LeagueRow({ league, view, onOpen, onDelete }: LeagueRowProps) {
  const hasActionCell = view === "UPCOMING" && onDelete != null;
  const canDelete =
    hasActionCell && league.status === Clubhouse_LeagueV2Status.DRAFT;
  const tile = tileFor(league.type, league.scoringFormat);
  const isScramble = league.type === Clubhouse_LeagueType.SCRAMBLE;
  const totalRounds = league.statusSummary?.totalRounds ?? 0;
  const roundsFinalized = league.statusSummary?.roundsFinalized ?? 0;
  const currentRoundNumber = league.statusSummary?.currentRoundNumber ?? 1;
  const hasOpenRound = league.roundSchedule?.openRoundNumber != null;
  const { finalizedPct, currentPct } = progressPercent(
    roundsFinalized,
    totalRounds,
    hasOpenRound,
  );
  const seasonComplete = totalRounds > 0 && finalizedPct >= 100;

  // The bar grows from nothing on first paint, once. Starting at the final
  // value and transitioning from it would animate on every re-render; this
  // flips after mount so the transition has somewhere to travel from.
  const [barGrown, setBarGrown] = useState(false);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setBarGrown(true));
    return () => cancelAnimationFrame(frame);
  }, []);
  const seasonDates = formatSeasonWindow(
    league.startDateUtc as unknown as string,
    league.endDateUtc as unknown as string,
    league.schedulingTimezone,
  );
  const status = formatStatusLine(league, view);

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={league.name}
      onClick={() => onOpen(league.leagueId)}
      onKeyDown={(e) => {
        if (e.key !== "Enter" && e.key !== " ") return;
        // Space scrolls the page by default; a real button would not.
        e.preventDefault();
        onOpen(league.leagueId);
      }}
      className={cn(
        "group grid cursor-pointer grid-cols-[2.75rem_1fr_auto] items-center gap-x-3 gap-y-2 px-4 py-4 transition-colors duration-150 hover:bg-muted/50 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring active:bg-muted md:gap-4 md:px-6 md:py-5",
        hasActionCell ? GRID_COLS.withAction : GRID_COLS.withoutAction,
      )}
    >
      <div
        className={cn(
          "flex size-11 items-center justify-center rounded-xl text-xs font-bold",
          tile.bg,
          tile.text,
        )}
      >
        {tile.letters}
      </div>

      <div className="min-w-0">
        <div
          className="truncate text-base font-extrabold text-ink"
          title={league.name}
        >
          {league.name}
        </div>
        <div className="mt-1 flex gap-1.5">
          {!isScramble && (
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium">
              {league.type != null ? leagueTypeLabel[league.type] : "—"}
            </span>
          )}
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium">
            {isScramble
              ? "Scramble"
              : league.scoringFormat != null
                ? FORMAT_TAG_LABEL[league.scoringFormat]
                : "—"}
          </span>
        </div>
        <div className="mt-1 flex items-center gap-1 text-xs text-text-muted">
          <MapPin className="size-3" />
          {league.facilityName ?? "—"}
        </div>
      </div>

      <div className="max-md:hidden">
        {view === "UPCOMING" ? (
          <>
            <div className="text-sm tabular-nums text-text-faint">
              {totalRounds} rounds
            </div>
            <div className="mt-1 text-xs text-text-faint">{seasonDates}</div>
          </>
        ) : totalRounds === 0 ? (
          <>
            <div className="text-sm text-text-faint">No rounds scheduled</div>
            <div className="mt-1 text-xs text-text-faint">{seasonDates}</div>
          </>
        ) : (
          <>
            <div className="text-sm tabular-nums">
              <span className="font-semibold text-ink">
                Round {view === "IN_SEASON" ? currentRoundNumber : totalRounds}
              </span>{" "}
              <span className="text-text-faint">of {totalRounds}</span>
            </div>
            {/* Gold sits behind and runs one round further than green, so the
                green layer masks it back to exactly the open round's width.
                Both scale rather than resize, to stay off the layout path. */}
            <div className="relative mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="absolute inset-0 origin-left bg-progress-live transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none"
                style={{
                  transform: `scaleX(${barGrown ? (finalizedPct + currentPct) / 100 : 0})`,
                }}
              />
              <div
                className="absolute inset-0 origin-left bg-good transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none"
                style={{
                  transform: `scaleX(${barGrown ? finalizedPct / 100 : 0})`,
                }}
              />
              {seasonComplete && (
                <div
                  className="season-finished-sweep pointer-events-none absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-transparent via-white/50 to-transparent motion-reduce:hidden"
                  aria-hidden="true"
                />
              )}
            </div>
            <div className="mt-1 text-xs text-text-faint">{seasonDates}</div>
          </>
        )}
      </div>

      <div className="flex items-center gap-2 max-md:col-start-2 max-md:row-start-2">
        <span
          className={cn(
            "mt-1 size-2 shrink-0 self-start rounded-full",
            DOT_CLASS[status.dot],
            status.dot === "gold" && "open-round-breath",
          )}
        />
        <div>
          <div className="text-sm font-semibold text-ink">{status.line1}</div>
          {status.line2 && (
            <div className="text-xs text-text-faint">{status.line2}</div>
          )}
        </div>
      </div>

      {hasActionCell && (
        // On phones the status drops to the second line, so the action sits
        // beside it under the arrow rather than adding a column.
        <div className="flex justify-center max-md:col-start-3 max-md:row-start-2">
          {canDelete && (
            <DeleteDraftButton
              variant="row"
              leagueName={league.name ?? "this league"}
              onClick={() => onDelete(league)}
            />
          )}
        </div>
      )}

      <ChevronRight
        className="size-4 text-text-faint transition-transform duration-150 group-hover:translate-x-0.5 motion-reduce:transition-none max-md:col-start-3 max-md:row-start-1 max-md:justify-self-end max-md:self-start"
        aria-hidden="true"
      />
    </div>
  );
}
